import { useCallback, useMemo, useState } from 'react';

/**
 * Multi-select state for catalog lists (products / categories / manufacturers).
 * Selection is by ID so it survives re-sorting and filtering; `prune` drops
 * IDs that no longer exist after a refetch or delete.
 */
export function useCatalogSelection() {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  const isSelected = useCallback((id: string) => selected.has(id), [selected]);

  const toggle = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /** Select every id in `ids`, or clear them all if every one is already selected. */
  const toggleAll = useCallback((ids: string[]) => {
    setSelected(prev => {
      const allSelected = ids.length > 0 && ids.every(id => prev.has(id));
      const next = new Set(prev);
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  /** Keep only ids that still exist. */
  const prune = useCallback((existingIds: Iterable<string>) => {
    const keep = new Set(existingIds);
    setSelected(prev => {
      const next = new Set([...prev].filter(id => keep.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, []);

  const selectedIds = useMemo(() => [...selected], [selected]);

  return {
    selected,
    selectedIds,
    count: selected.size,
    isSelected,
    toggle,
    toggleAll,
    clear,
    prune,
  };
}
